'use client';

import { useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';

interface ShortcutConfig {
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  description: string;
  action: () => void;
}

interface UseKeyboardShortcutsOptions {
  onOpenSearch?: () => void;
  onNewCall?: () => void;
  onToggleDark?: () => void;
  onOpenCommand?: () => void;
}

export function useKeyboardShortcuts({
  onOpenSearch,
  onNewCall,
  onToggleDark,
  onOpenCommand,
}: UseKeyboardShortcutsOptions = {}) {
  const router = useRouter();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable;

      const isModifier = e.ctrlKey || e.metaKey;

      if (isInput && !isModifier && e.key !== 'Escape') return;

      const shortcuts: ShortcutConfig[] = [
        // Global search: Ctrl+K or Cmd+K
        {
          key: 'k',
          ctrl: true,
          description: 'Apri ricerca globale',
          action: () => onOpenSearch?.(),
        },
        // Command palette: Ctrl+P
        {
          key: 'p',
          ctrl: true,
          description: 'Apri command palette',
          action: () => onOpenCommand?.(),
        },
        // Also support "/" for search (not in inputs)
        {
          key: '/',
          description: 'Apri ricerca globale',
          action: () => !isInput && onOpenSearch?.(),
        },
        // Navigation shortcuts (not in inputs)
        {
          key: 'd',
          description: 'Vai alla Dashboard',
          action: () => !isInput && router.push('/call-dashboard'),
        },
        {
          key: 'p',
          description: 'Vai ai Pazienti',
          action: () => !isInput && router.push('/patient-management'),
        },
        {
          key: 'c',
          description: 'Vai al Calendario',
          action: () => !isInput && router.push('/calendar'),
        },
        {
          key: 'h',
          description: 'Vai allo Storico Chiamate',
          action: () => !isInput && router.push('/call-history'),
        },
        {
          key: 'a',
          description: 'Vai alle Analytics',
          action: () => !isInput && router.push('/analytics'),
        },
        {
          key: 'n',
          description: 'Nuova chiamata',
          action: () => !isInput && onNewCall?.(),
        },
        // Dark mode toggle: Ctrl+Shift+D
        {
          key: 'd',
          ctrl: true,
          shift: true,
          description: 'Toggle dark mode',
          action: () => onToggleDark?.(),
        },
      ];

      for (const shortcut of shortcuts) {
        const ctrlMatch = shortcut.ctrl ? (e.ctrlKey || e.metaKey) : (!e.ctrlKey && !e.metaKey);
        const shiftMatch = shortcut.shift ? e.shiftKey : !e.shiftKey;
        const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase();

        if (keyMatch && ctrlMatch && shiftMatch) {
          if (shortcut.ctrl || shortcut.meta) {
            e.preventDefault();
          }
          shortcut.action();
          break;
        }
      }
    },
    [router, onOpenSearch, onNewCall, onToggleDark, onOpenCommand]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

// Keyboard shortcuts help data for display
export const KEYBOARD_SHORTCUTS = [
  { keys: ['Ctrl', 'K'], description: 'Ricerca globale' },
  { keys: ['Ctrl', 'P'], description: 'Command palette' },
  { keys: ['/'], description: 'Ricerca globale' },
  { keys: ['D'], description: 'Dashboard' },
  { keys: ['P'], description: 'Pazienti' },
  { keys: ['C'], description: 'Calendario' },
  { keys: ['H'], description: 'Storico Chiamate' },
  { keys: ['A'], description: 'Analytics' },
  { keys: ['N'], description: 'Nuova chiamata' },
  { keys: ['Ctrl', 'Shift', 'D'], description: 'Toggle dark mode' },
];
